import PropTypes from "prop-types"
import { Alert, Grid } from "@mui/material"
import { useEffect, useState } from "react"
import { set, useFormContext } from "react-hook-form"
import Dropdown from "components/fields/Dropdown"
import { assignCampaign } from "services/assignCampaign"
import Creneaux from "components/Creneaux"
import { useForms } from "hooks/useForm"
import { academicLevelValues, campaignStatus, formatEvent, getCampusLabel } from "services"

// EligibleAcademicLevel__c est un picklist multiple Salesforce: valeurs séparées par des points-virgules
const parseEligibleAcademicLevels = (value) => (value || '')
  .split(';')
  .map(level => level.trim())
  .filter(level => level !== '' && academicLevelValues.includes(level))

// Une campagne sans niveau éligible renseigné est ouverte à tous les niveaux
const isEligibleForAcademicLevel = (campaign, academicLevel) => {
  const levels = parseEligibleAcademicLevels(campaign.EligibleAcademicLevel__c)
  return levels.length === 0 || levels.includes(academicLevel)
}

// Faute de niveau éligible connu, on laisse le backend valider tous les niveaux réellement disponibles pour l'école (plutôt qu'un sous-ensemble arbitraire)
const defaultAcademics = academicLevelValues.map(level => ({
  label: level,
  value: level
}))

const toAcademicOptions = (academics) => academics.length
  ? academics.map(level => ({
    label: level,
    value: level
  }))
  : defaultAcademics

const Campaigns = ({ contact = true, division = '', last_form, limit = 20, native = false, school, setAcademicLevels, text, textAvailability, type }) => {

  const { control, setValue, watch } = useFormContext()
  const { componentFetched, recordTypeId, salesforce, setComponentStates, setOpenSnackbar, setSnackMessage } = useForms()
  const academicLevel = watch('lead.AcademicLevel__c')
  const campus = watch('lead.Campus__c')
  const campaignNational = watch('campaignNational')
  const campaignId = watch('campaignId')

  const [campaigns, setCampaigns] = useState([])
  const [optionsCampaigns, setOptionsCampaigns] = useState([])
  const [campaignFetched, setCampaignFetched] = useState(false)

  useEffect(() => {
    setComponentStates((prevState) => [
      ...prevState,
      { name: 'campaigns', added: true, fetched: false },
    ])
  }, [setComponentStates])

  useEffect(() => {
    if (setAcademicLevels !== undefined && (campaignId === undefined || campaignId === '')) {
      const firstValuesByCampus = campaigns.reduce((result, item) => {
        const campus = item.Campus__c
        result[campus] = result[campus] || []
        if (result[campus].length < limit) {
          result[campus].push({
            Id: item.Id,
            EligibleAcademicLevel__c: parseEligibleAcademicLevels(item.EligibleAcademicLevel__c)
          })
        }
        return result
      }, {})

      // On retire le [campus]
      const campaignsAcademics = Object.values(firstValuesByCampus).flat()

      // On doit savoir si les campagnes sont dispos selon tel niveau académique
      // Si une campagne n'a pas de niveau renseigné, elle est ouverte à tous : on propose alors tous les niveaux de l'école
      const academics = campaignsAcademics.some(campaign => campaign.EligibleAcademicLevel__c.length === 0)
        ? []
        : [...new Set(campaignsAcademics.flatMap(campaign => campaign.EligibleAcademicLevel__c))]

      const finalAcademics = toAcademicOptions(academics)

      // On envoi enfin à la fonction pour l'envoyer à l'enfant
      if (finalAcademics.length > 0) {
        setAcademicLevels(finalAcademics)
      } else {
        // setAcademicLevels(undefined)
      }
    }
    if (campaignId !== undefined && campaignId !== '' && campaigns.length > 0 && setAcademicLevels !== undefined) {
      console.log('on a mis un id de campagneId')
      const campaignDetail = campaigns.find(el => el.Id === campaignId)
      if (campaignDetail !== undefined) {
        const academics = parseEligibleAcademicLevels(campaignDetail.EligibleAcademicLevel__c)
        setAcademicLevels(toAcademicOptions(academics))
      } else {
        // setAcademicLevels(undefined)
      }
    }
  }, [campaigns, campaignId])

  useEffect(() => {
    if (recordTypeId !== '') {
      const getData = async () => {
        const campaigns = await salesforce.getCampaigns(division, school, type)

        return { campaigns }
      }

      getData().then((value) => {
        const { campaigns } = value

        setCampaigns(campaigns)
        // On vérifie si il existe pas un paramètre CampaignId, si oui on assigne les bonnes valeurs sur le formulaire
        assignCampaign(campaigns, setValue)
        componentFetched('campaigns')
        setCampaignFetched(true)
      }).catch((error) => {
        setOpenSnackbar(true)
        console.log('erreur', error)
        setSnackMessage('Une erreur est survenue lors du chargement du formulaire')
      })
    }
  }, [recordTypeId])

  useEffect(() => {
    if (salesforce !== null && campaignFetched) {
      if (campus !== '' && !campaignNational) {
        const currentCampaigns = campaigns.filter(el => (el.Campus__c === campus || (el.Campus__c === 'National' && (el.Detail__c === 'Webinar' || el.Detail__c === 'Autre'))) && (!setAcademicLevels || !academicLevelValues.includes(academicLevel) || isEligibleForAcademicLevel(el, academicLevel))).slice(0, limit)
        // On ne vide la campagne sélectionnée (manuellement ou via paramètre) que si elle ne fait plus partie des choix (autre campus ou niveau non éligible)
        if (!currentCampaigns.some(el => el.Id === campaignId)) {
          setValue('campaignId', '')
        }
        setFinalCampaigns(currentCampaigns)
      } else if (!campaignNational) {
        setValue('campaignId', '')
      } else {
        const currentCampaigns = campaigns.filter(el => el.Campus__c === 'National').slice(0, limit)
        setFinalCampaigns(currentCampaigns)
      }
    }
  }, [academicLevel, campaignNational, campaignFetched, campaigns, campus, salesforce])

  useEffect(() => {
    if (campaignId !== '' && campaignId !== 'unavailable') {
      const finalCampaigns = formatEvent(campaigns, contact)
      const campaign = finalCampaigns.find(el => el.value === campaignId)
      let eventDateAndDuration = ''
      if (campaign !== undefined) {
        eventDateAndDuration = campaign.dateDuration
      }
      setValue('lead.EventDateAndDuration__c', eventDateAndDuration)
      const campaignSalesforce = campaigns.find(el => el.Id === campaignId)
      setValue('lead.Labelcampaign__c', campaignSalesforce.Label__c)
      let lastForm = ''
      if (last_form !== undefined) {
        lastForm = last_form
      } else if (campaignSalesforce !== undefined) {
        lastForm = campaignSalesforce.Detail__c
      }
      if (campaignSalesforce.IsOnline__c){
        lastForm += ' distanciel'
      }
      setValue('lead.LastForm__c', lastForm)

      const status = campaignStatus.find(el => el.online === campaignSalesforce?.IsOnline__c)?.name
      const campaignCustomText = campaignSalesforce?.CustomText__c
      const campaignWebinarUrl = campaignSalesforce?.Webinar_URL__c

      setValue('lead.CampaignCustomText__c', campaignCustomText)
      setValue('lead.WebinarUrlCampaign__c', campaignWebinarUrl)
      setValue('status', status)
      setValue('slotId', '')
    }
  }, [campaignId])

  const setFinalCampaigns = (currentCampaigns) => {
    const finalCampaigns = formatEvent(currentCampaigns, contact)
    setOptionsCampaigns(finalCampaigns)
    if (!campaignNational) {
      assignCampaign(currentCampaigns, setValue)
    }
    if (currentCampaigns.length === 0) {
      setValue('campaignId', 'unavailable')
    }
    // setValue('slotId', '')
  }

  return (
    <>
      {((optionsCampaigns.length > 0 && !contact) || (optionsCampaigns.length > 1 && contact) || campus === '') && (
        <Grid item xs={12}>
          <Dropdown control={control} disabled={campus === '' && !campaignNational} label={`Je souhaite participer ${text}`} name="campaignId" native={native} options={optionsCampaigns} required />
        </Grid>
      )}
      <Creneaux campaigns={campaigns} native={native} />
      {!contact && optionsCampaigns.length === 0 && campus !== '' && (
        <Grid item xs={12}>
          <Alert color="info">
            Il n'y a plus de dates {textAvailability} à {getCampusLabel(campus)}.
          </Alert>
        </Grid>
      )}
    </>
  )
}

Campaigns.propTypes = {
  contact: PropTypes.bool,
  division: PropTypes.string,
  last_form: PropTypes.string,
  limit: PropTypes.number,
  native: PropTypes.bool,
  school: PropTypes.string,
  setAcademicLevels: PropTypes.func,
  text: PropTypes.string,
  textAvailability: PropTypes.string,
  type: PropTypes.string
}

export default Campaigns